import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogIn, LogOut, ShieldCheck, Building2, UserCog, KeyRound, Loader2, HeartHandshake, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { displayName } from "@/lib/format";
import { officerHasConsole, officerAllowedModules } from "@/lib/access";
import { toast } from "sonner";
import StaffClockTile from "@/components/StaffClockTile";
import { ClientPinDialog } from "@/components/care/ClientPinDialog";
import useWakeLock from "@/hooks/useWakeLock";
import { FitToScreen } from "@/components/FitToScreen";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function KioskHome() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [clientPin, setClientPin] = useState(false);
  const [building, setBuilding] = useState({ name: "", address: "", phone: "", logo_data_url: "" });
  const [companyType, setCompanyType] = useState("business");
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Keep the tablet awake while the kiosk is open.
  useWakeLock(true);

  useEffect(() => {
    api.get("/me/company").then(({ data }) => setCompanyType(data?.type || "business")).catch(() => {});
    api.get("/settings/building")
      .then(({ data }) => setBuilding({ ...data, logo_data_url: data.effective_logo_data_url || data.logo_data_url || "" }))
      .catch(() => {});
  }, []);

  const confirmEndShift = async (e) => {
    e?.preventDefault();
    if (!pwd || verifying) return;
    setVerifying(true);
    try {
      await api.post("/auth/verify-password", { password: pwd });
      await logout();
      toast.success("Shift ended");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect password");
    } finally {
      setVerifying(false);
    }
  };

  const openSignOut = () => {
    setPwd("");
    setSignOutOpen(true);
  };

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      className="relative h-screen w-screen overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 15% 10%, rgba(37,99,235,0.18), transparent 60%)," +
          "radial-gradient(900px 500px at 95% 90%, rgba(14,165,233,0.18), transparent 55%)," +
          "linear-gradient(135deg, #f8fafc 0%, #eef2ff 55%, #e0f2fe 100%)",
      }}
      data-testid="kiosk-home"
    >
      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.06) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(15,23,42,0.06) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.9) 30%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.9) 30%, transparent 75%)",
        }}
        aria-hidden
      />
      {/* Floating blobs */}
      <div
        className="absolute -top-32 -left-32 h-96 w-96 rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, #93c5fd, transparent)" }}
        aria-hidden
      />
      <div
        className="absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(closest-side, #67e8f9, transparent)" }}
        aria-hidden
      />

      <div className="relative z-10 flex h-full flex-col safe-area">
        {/* Top bar */}
        <header className="flex items-center justify-between px-12 py-8 [@media(max-height:820px)]:py-4">
          <div className="flex items-center gap-3 rounded-full bg-white/80 px-5 py-2.5 backdrop-blur-xl border border-white/60 shadow-sm">
            {building.logo_data_url ? (
              <img src={building.logo_data_url} alt="" className="h-5 w-5 object-contain" />
            ) : (
              <img src="/logerd-logo.png" alt="" className="h-5 w-5 object-contain" />
            )}
            <span className="font-display font-semibold tracking-tight text-slate-900">
              {building.name || "Logerd"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 border border-emerald-200 shadow-sm"
              data-testid="on-duty-badge"
            >
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <UserCog className="h-4 w-4 text-emerald-700" />
              <span className="text-sm">
                <span className="text-emerald-700 font-medium">On duty:</span>{" "}
                <span className="font-semibold text-slate-900">{displayName(user?.name)}</span>
              </span>
            </div>
            {officerHasConsole(user) && (
              <button type="button" onClick={() => nav(officerAllowedModules(user)[0].path)} data-testid="kiosk-console-btn" title="Open my console"
                className="flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 border border-white/60 shadow-sm text-sm font-medium text-slate-700 hover:bg-white transition backdrop-blur-xl">
                <LayoutDashboard className="h-4 w-4 text-primary" /> Console
              </button>
            )}
            <button
              type="button"
              onClick={openSignOut}
              data-testid="kiosk-officer-signout-btn"
              aria-label="Officer sign out"
              title="Officer sign out"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/70 border border-white/60 text-slate-500 hover:text-slate-900 hover:bg-white transition backdrop-blur-xl shadow-sm"
            >
              <KeyRound className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Center card */}
        <main className="flex flex-1 min-h-0 items-center justify-center px-6 pb-6 [@media(max-height:820px)]:pb-3">
          <FitToScreen maxWidth={768} testid="kiosk-fit" className="rounded-3xl border border-white/60 bg-white/85 p-10 md:p-14 backdrop-blur-2xl shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25)]">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-primary mb-4">
              <Building2 className="h-4 w-4" />
              <span>Reception · Sign-in Required by Law</span>
            </div>

            {(building.name || building.logo_data_url) && (
              <div className="mb-4 flex items-start gap-4" data-testid="kiosk-building-name">
                {building.logo_data_url && (
                  <img
                    src={building.logo_data_url}
                    alt={building.name || "Logo"}
                    className="h-14 w-14 object-contain shrink-0"
                  />
                )}
                <div>
                  {building.name && (
                    <div className="font-display text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">
                      {building.name}
                    </div>
                  )}
                  {building.address && (
                    <div className="text-sm text-slate-600 whitespace-pre-line mt-1 max-w-md">
                      {building.address}
                    </div>
                  )}
                </div>
              </div>
            )}

            <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
              Welcome.
            </h1>
            <p className="mt-4 text-xl lg:text-2xl text-slate-700 max-w-xl">
              Please sign in or out below. All visitors and contractors must be logged for safety and compliance.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Link
                to="/kiosk/checkin"
                data-testid="kiosk-checkin-btn"
                className="group flex flex-col items-start justify-between gap-6 rounded-2xl bg-primary px-8 py-8 text-white shadow-md transition active:scale-[0.98] hover:shadow-lg min-h-[180px]"
              >
                <LogIn className="h-12 w-12" strokeWidth={2} />
                <div>
                  <div className="font-display text-3xl font-semibold tracking-tight">Check In</div>
                  <div className="text-sm uppercase tracking-widest text-white/80 mt-1">Arriving now</div>
                </div>
              </Link>

              <Link
                to="/kiosk/checkout"
                data-testid="kiosk-checkout-btn"
                className="group flex flex-col items-start justify-between gap-6 rounded-2xl border-2 border-slate-900/90 bg-white px-8 py-8 text-slate-900 transition active:scale-[0.98] hover:bg-slate-900 hover:text-white min-h-[180px]"
              >
                <LogOut className="h-12 w-12" strokeWidth={2} />
                <div>
                  <div className="font-display text-3xl font-semibold tracking-tight">Check Out</div>
                  <div className="text-sm uppercase tracking-widest opacity-70 mt-1">Leaving the building</div>
                </div>
              </Link>

              <StaffClockTile variant="tile" wide={companyType !== "care"} />
              {companyType === "care" && (
                <button type="button" onClick={() => setClientPin(true)} data-testid="kiosk-clients-tile"
                  className="group flex flex-col items-start justify-between gap-6 rounded-2xl border-2 border-emerald-600 bg-emerald-600 px-8 py-8 text-left text-white transition active:scale-[0.98] hover:bg-emerald-700 hover:border-emerald-700 min-h-[180px]">
                  <HeartHandshake className="h-12 w-12" strokeWidth={2} />
                  <div>
                    <div className="font-display text-3xl font-semibold tracking-tight">Clients</div>
                    <div className="text-sm uppercase tracking-widest opacity-80 mt-1">Tap, enter your PIN · check in & out</div>
                  </div>
                </button>
              )}
              <ClientPinDialog open={clientPin} onClose={() => setClientPin(false)}
                onIdentified={(staff, pin) => { setClientPin(false); nav("/kiosk/clients", { state: { staff, pin } }); }} />
            </div>
          </FitToScreen>
        </main>

        {/* Footer */}
        <footer className="px-12 py-6 [@media(max-height:820px)]:py-3 text-sm text-slate-700">
          <span className="rounded-full bg-white/70 px-4 py-1.5 backdrop-blur-xl border border-white/50">
            Safety & Compliance Log · {today}
          </span>
        </footer>
      </div>

      {/* Officer sign-out dialog (password-confirmed) */}
      <Dialog open={signOutOpen} onOpenChange={setSignOutOpen}>
        <DialogContent className="sm:max-w-md" data-testid="kiosk-signout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> Officer sign out
            </DialogTitle>
            <DialogDescription>
              Enter your password to end your shift on this device.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={confirmEndShift} className="space-y-3 pt-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span className="text-slate-500">On duty: </span>
              <span className="font-semibold text-slate-900">{displayName(user?.name)}</span>
              <span className="text-slate-500"> · {user?.email}</span>
            </div>
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Password</span>
              <input
                type="password"
                autoFocus
                required
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                data-testid="kiosk-signout-password"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <DialogFooter className="pt-2 gap-2 sm:gap-2">
              <button
                type="button"
                onClick={() => setSignOutOpen(false)}
                data-testid="kiosk-signout-cancel"
                className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={verifying || !pwd}
                data-testid="kiosk-signout-confirm"
                className="h-10 inline-flex items-center justify-center gap-2 rounded-lg bg-destructive px-4 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60 transition"
              >
                {verifying && <Loader2 className="h-4 w-4 animate-spin" />}
                End shift
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
